/**
 * Die Reißleine.
 *
 * „Das Backoffice muss laufen" heißt nicht, dass nie ein Fehler passiert —
 * das kann niemand versprechen. Es heißt: ein Fehler an einer Stelle darf
 * nicht die ganze Seite weiß machen. Ohne diese Grenze führt ein einziger
 * fehlender Wert in einer Tabellenzeile dazu, dass React den kompletten Baum
 * abräumt und im Büro ein leeres Fenster steht — ohne jeden Hinweis, was zu
 * tun ist.
 *
 * Mit der Grenze bleibt die Navigation stehen, der Fehler ist auf einen
 * Bildschirm begrenzt, und der Originaltext ist ausklappbar. Genau den braucht
 * man, wenn im Büro jemand anruft und beschreibt, was er sieht.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

type Zustand = { problem: Error | null; woher: string | null };

export class Fehlergrenze extends Component<
  { children: ReactNode; bereich?: string },
  Zustand
> {
  state: Zustand = { problem: null, woher: null };

  static getDerivedStateFromError(problem: Error): Partial<Zustand> {
    return { problem };
  }

  componentDidCatch(problem: Error, hinweis: ErrorInfo) {
    // Auf die Konsole, damit die Meldung beim Nachschauen auffindbar ist.
    console.error('Fehler in der Oberfläche:', problem, hinweis.componentStack);
    this.setState({ woher: hinweis.componentStack ?? null });
  }

  render() {
    if (!this.state.problem) return this.props.children;

    return (
      <div className="stapel" style={{ padding: 20 }}>
        <div className="meldung meldung--fehler">
          <div>
            <strong>
              {this.props.bereich
                ? `Der Bereich „${this.props.bereich}" ist abgestürzt`
                : 'Etwas ist abgestürzt'}
            </strong>
            Der Rest des Backoffice läuft weiter. Lade die Seite neu. Kommt der
            Fehler wieder, gib den Text unten weiter — daran lässt er sich
            finden.
          </div>
          <button
            className="meldung__knopf"
            onClick={() => window.location.reload()}
          >
            Seite neu laden
          </button>
        </div>

        <details>
          <summary className="meta" style={{ cursor: 'pointer' }}>
            Technische Einzelheiten
          </summary>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: 13,
              color: 'var(--text-meta)',
              marginTop: 8,
            }}
          >
            {this.state.problem.message}
            {this.state.woher}
          </pre>
        </details>
      </div>
    );
  }
}
